define('services/intervalService',[
    'lib/lodash4',
    'lib/knockout',
    'moment',
    'services/loggingService'
], function (
    _,
    ko,
    moment,
    log
) {
    'use strict';

    // ===================================================================================
    // Private Variables
    // ===================================================================================

    var currentInterval = ko.observable();

    // ===================================================================================
    // Private Methods
    // ===================================================================================

    /**
     * generate the current 30 minute interval
     * @return {String} start and end interval string
     */
    var generateCurrentInterval = function() {
        var end, start = moment();
        if (start.minutes() < 30) {
            start.startOf('hour');
            end = moment(start).minutes(30).startOf('minute');
        } else {
            start.minutes(30).startOf('minute');
            end = moment(start).add(1, 'hours').startOf('hour');
        }
        var interval = start.toISOString() + "/" + end.toISOString();
        return interval;
    };

    /**
     * checks to see if the current interval is up
     * @param  {String}   interval - A ko observable representing the interval you want to check against
     * @param  {Function} callback - A callback function to execute when the interval is up
     */
    var isIntervalExpired = function(interval) {

        var intervals = ko.unwrap(interval);
        var endDate   = moment(intervals.split("/")[1]);
        var currentMoment = moment();
        var diff = currentMoment.diff(endDate, 'seconds');
        var intervalToAdd = {seconds: 5, milliseconds: 5000};

        var timerID = setInterval(function() {

            diff = diff + intervalToAdd.seconds;

            /**
             * If the interval runs out, recreate the interval and reset the
             * observable (self.interval). Also reset the endDate so that we
             * can continue comparing.
             */

            if (diff > 0) {
                clearInterval(timerID);
                currentInterval(generateCurrentInterval());
            }


        }, intervalToAdd.milliseconds);

    };

    // ===================================================================================
    // Service Declaration
    // ===================================================================================

    var IntervalService = function () {
        var self = this;

        /**
         * Get the default interval length, in minutes.
         *
         * @return {Integer} The default length of intervals in minutes.
         */
        self.DEFAULT_INTERVAL_LENGTH = _.constant(30);

        /**
         * Get the ISO 8601 interval string for the current 30 minute interval.
         * @return {String}
         */
        self.currentInterval = ko.computed(function () {
            // wrap the internal observable in a computed to ensure that it is read-only
            return "" + currentInterval();
        });

        /**
         * Get the start time for the current interval.
         *
         * @return {String} An ISO8601 string representing the start time of the current
         *  interval.
         */
        self.currentIntervalStart = ko.computed(function () {
            if (self.currentInterval()) {
                return self.currentInterval().split("/")[0];
            }
        });

        self.dayIntervalBeforeCurrentIntervalStart = ko.computed(function () {
            var startMoment = moment(self.currentIntervalStart()).startOf("day");
            var endMoment = moment(self.currentIntervalStart());

            return startMoment.toISOString() + '/' + endMoment.toISOString();
        });

        /**
         * Get the ISO 8601 interval string for the current day interval.
         * @return {String}
         */
        self.currentDayInterval = ko.computed(function () {
            var startMoment = moment(self.currentIntervalStart()).startOf("day");
            var endMoment = moment(startMoment).add(1, "days").startOf("day");

            return startMoment.toISOString() + "/" + endMoment.toISOString();
        });

        /**
         * Get the ISO 8601 interval string for yesterday.
         * @return {String}
         */
        self.yesterdayInterval = ko.computed(function () {
            var startMoment = moment().subtract(1, 'days').startOf("day");
            var endMoment = moment().startOf("day");

            return startMoment.toISOString() + "/" + endMoment.toISOString();
        });

        /**
         * Get the ISO 8601 interval string for the current month interval.
         * @return {String}
         */
        self.currentMonthInterval = ko.computed(function () {
            var startMonth = moment(self.currentIntervalStart()).startOf("month");
            var endMoment = moment(startMonth).add(1, "months").startOf("month");
            return startMonth.toISOString() + "/" + endMoment.toISOString();
        });

        self.currentWeekInterval = ko.computed(function () {
            var startWeek = moment(self.currentIntervalStart()).startOf("week");
            var endMoment = moment(startWeek).add(1, "weeks").startOf("week");
            return startWeek.toISOString() + "/" + endMoment.toISOString();
        });

        self.convertIntervalStringToMoments = function (intervalStr) {
            var intervals = self.splitIntervalString(ko.unwrap(intervalStr));

            return {
                start: moment(intervals[0]),
                end: moment(intervals[1])
            };
        };

        self.splitIntervalString = function (intervalStr) {
            if (!_.isString(intervalStr) || !intervalStr) {
                throw new Error("splitIntervalString() - intervalStr is required to be a string and have a value.");
            }

            var intervals = intervalStr.split("/");

            if (intervals.length != 2 || !intervals[0] || !intervals[1]) {
                // The argument string was not in the expected format
                log.error("getDataForInterval() - The string argument was not in the valid ISO8601 interval format");
                return null;
            }

            return intervals;
        };

        /**
         * Get the ISO 8601 interval string for the start of the current month up to
         * the start of the current day. For example, assuming that date is
         * August 14, 2014, then the interval returned will be:
         *
         * 2014-08-01T04:00:00.000Z/2014-08-14T04:00:00.000Z
         *
         * @return {String}
         */
        self.startOfMonthToStartOfCurrentDayInterval = ko.computed(function () {
            var startMonth = moment(self.currentIntervalStart()).startOf('month').startOf('day');
            var end = moment(self.currentIntervalStart()).startOf("day");
            return startMonth.toISOString() + "/" + end.toISOString();
        });
    };

    // ===================================================================================
    // Service Init Logic
    // ===================================================================================

    currentInterval(generateCurrentInterval());

    ko.computed(function () {
        // any time the interval changes, ensure that we set up a timer to validate when
        // the new interval will expire
        isIntervalExpired(currentInterval());
    });

    var intervalService = new IntervalService();

    return intervalService;
});

